import { useContext, useState, useRef, useEffect } from 'react';
import { useOne, BaseRecord } from '@refinedev/core';
import { FacilityContext } from '../../components/layout';
import Loading from '../../components/loading/loading';
import { 
  BuildingOffice2Icon, 
  EnvelopeIcon, 
  PhoneIcon, 
  MapPinIcon,
  ClipboardDocumentIcon,
  CheckIcon,
  ExclamationTriangleIcon,
  UserIcon,
  GlobeAltIcon,
  HashtagIcon,
  MapIcon,
  CameraIcon,
  PhotoIcon,
  UsersIcon, // Added for the new table
  PencilIcon,
  TrashIcon,
  PlusIcon
} from '@heroicons/react/24/outline';
import { 
  CheckCircleIcon,
  ExclamationCircleIcon,
  XCircleIcon
} from '@heroicons/react/24/solid';
import useApiService from '../../hooks/useApiService'; // Import the useApiService hook

// Color palette (unchanged)
const colors = {
  primary: "#2A333C",
  primaryLight: "#677D94",
  primaryDark: "#1A2128",
  secondary: "#F5B957",
  secondaryLight: "#FCECD1",
  secondaryDark: "#AB6D0A",
  conPrimary: "#1F2937",
  iconSecondary: "#7A6646",
  iconTertiary: "#94A3B8",
  iconFourth: "#CBD5E1",
  iconInverted: "#FFFFFF",
  badgePrimary: "#FF6B6B",
  green100: "#E0EDE2",
  green600: "#3A7D44",
  green800: "#2E6B3A",
  blue100: "#E0E8ED",
  blue500: "#4A6FA5",
  blue700: "#3A5F7D",
  blue800: "#2A4A6C",
  blue900: "#1A334C",
  yellow100: "#FFF8E6",
  yellow800: "#D4A123",
  red100: "#FFEAEA",
  red500: "#C84648",
  red700: "#A23537",
  red900: "#7A2527",
  orange50: "#FFF5EB",
  orange100: "#FCE8D9",
  orange500: "#E67E34",
  orange700: "#C4450D",
  orange800: "#BF5A1F",
  card1: "#F8FAFC",
};

// Type definitions (unchanged from previous version)
interface Organization extends BaseRecord {
  organization_id: string;
  organization_name: string;
  email: string | null;
  phone: string | null;
  address1: string;
  address2: string | null;
  city: string;
  state: string;
  zip: string;
}

interface Person {
  person_id: string;
  firstname: string;
  lastname: string;
  phone: string | null;
  photo: string | null;
  email: string | null;
}

interface User extends BaseRecord {
  user_id: string;
  status: string;
  people: Person;
}

interface FacilityDetails {
  facility_detail_id: string;
  facility_id: string;
  instructions: string | null;
  description: string | null;
  created_at: string;
  updated_at: string;
  created_by: string;
  updated_by: string;
}

interface Facility extends BaseRecord {
  facility_id: string;
  facility_name: string;
  phone: string | null;
  email: string | null;
  address1: string;
  address2: string | null;
  city: string;
  state: string;
  zip: string;
  status: string;
  logo: string | null;
  organizations: Organization;
  location_code: string;
  market_id: string;
  facility_type_id: string;
  lat: string | null;
  long: string | null;
  users: User[];
}

// StatusBadge component (unchanged)
const StatusBadge = ({ status }: { status: string }) => {
  const statusConfig = {
    ACTIVE: { bg: colors.green100, text: colors.green800, icon: CheckCircleIcon },
    INACTIVE: { bg: colors.red100, text: colors.red700, icon: XCircleIcon },
    PENDING: { bg: colors.yellow100, text: colors.yellow800, icon: ExclamationCircleIcon },
  };
  
  const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.PENDING;
  const Icon = config.icon;
  
  return (
    <div 
      className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold"
      style={{ backgroundColor: config.bg, color: config.text }}
    >
      <Icon className="size-4" />
      {status}
    </div>
  );
};

// CopyToClipboard component (unchanged)
const CopyToClipboard = ({ text }: { text: string | null }) => {
  const [copied, setCopied] = useState(false);
  
  if (!text) return null;
  
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
          } catch (err) {
        // Failed to copy
      }
  };
  
  return (
    <button 
      onClick={handleCopy}
      className="rounded-lg p-2 transition-all duration-200 hover:bg-gray-100"
      style={{ color: copied ? colors.green600 : colors.iconTertiary }}
      title={copied ? 'Copied!' : 'Copy to clipboard'}
    >
      {copied ? <CheckIcon className="size-4" /> : <ClipboardDocumentIcon className="size-4" />}
    </button>
  );
};

// InfoCard component (unchanged)
const InfoCard = ({ 
  icon: Icon, 
  label, 
  value, 
  copyable = false, 
  className = "",
  fullWidth = false 
}: { 
  icon: React.ElementType;
  label: string;
  value: string | null;
  copyable?: boolean;
  className?: string;
  fullWidth?: boolean;
}) => (
  <div className={`group rounded-xl border border-gray-200 bg-white p-4 shadow-sm transition-all duration-200 hover:shadow-md sm:p-6 lg:p-4 ${fullWidth ? 'col-span-full' : ''} ${className}`}>
    <div className="flex items-start justify-between">
      <div className="flex flex-1 items-start gap-3 sm:gap-4 lg:gap-3">
        <div 
          className="rounded-lg p-2 transition-colors duration-200 group-hover:shadow-sm sm:p-3 lg:p-2"
          style={{ backgroundColor: colors.secondaryLight }}
        >
          <Icon className="size-4 sm:size-5 lg:size-4" style={{ color: colors.secondaryDark }} />
        </div>
        <div className="min-w-0 flex-1">
          <p className="mb-1 text-xs font-medium sm:text-sm lg:text-xs" style={{ color: colors.iconTertiary }}>{label}</p>
          <p className="break-all text-base font-semibold sm:text-lg lg:text-base" style={{ color: colors.primary }}>
            {value || 'N/A'}
          </p>
        </div>
      </div>
      {copyable && <CopyToClipboard text={value} />}
    </div>
  </div>
);

// SectionHeader component - Compact Desktop
const SectionHeader = ({ title, subtitle, icon: Icon }: { title: string; subtitle?: string; icon: React.ElementType }) => (
  <div className="mb-4 flex items-center gap-2 sm:mb-6 sm:gap-3 lg:mb-3 lg:gap-2">
    <div 
      className="rounded-lg p-2 sm:p-3 lg:p-2"
      style={{ backgroundColor: colors.primary }}
    >
      <Icon className="size-4 sm:size-6 lg:size-4" style={{ color: colors.iconInverted }} />
    </div>
    <div>
      <h3 className="text-lg font-bold sm:text-xl lg:text-base" style={{ color: colors.primary }}>{title}</h3>
      {subtitle && <p className="text-xs sm:text-sm lg:text-xs" style={{ color: colors.iconTertiary }}>{subtitle}</p>}
    </div>
  </div>
);

// ImageUpload component (unchanged)
interface ImageUploadProps {
  currentImage: string | null;
  facilityName: string;
  onImageChange: (file: File) => void;
}

const ImageUpload = ({ currentImage, facilityName, onImageChange }: ImageUploadProps) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleContainerClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file type to ensure only images are allowed
      const allowedTypes = [
        'image/png',
        'image/jpeg',
        'image/jpg',
        'image/gif',
        'image/webp',
        'image/bmp',
        'image/svg+xml'
      ];
      
      if (!allowedTypes.includes(file.type)) {
        alert('Please select only image files (PNG, JPG, JPEG, GIF, WebP, BMP, SVG). PDFs, DOC files, and other document types are not allowed.');
        // Clear the file input
        event.target.value = '';
        return;
      }
      
      // Validate file size (optional: limit to 5MB)
      const maxSize = 5 * 1024 * 1024; // 5MB
      if (file.size > maxSize) {
        alert('File size must be less than 5MB. Please select a smaller image file.');
        event.target.value = '';
        return;
      }
      
      onImageChange(file);
    }
  };

  return (
    <div 
      className="group relative size-32 cursor-pointer rounded-full" 
      onClick={handleContainerClick}
      title="Change facility logo"
    >
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileChange}
        className="hidden"
        accept=".png,.jpg,.jpeg,.gif,.webp,.bmp,.svg"
      />
      {currentImage ? (
        <img
          src={currentImage}
          alt={`${facilityName} logo`}
          className="size-full rounded-full border-4 border-white object-cover shadow-lg"
        />
      ) : (
        <div 
          className="flex size-full items-center justify-center rounded-full border-4 border-white shadow-lg"
          style={{ backgroundColor: colors.secondaryLight }}
        >
          <PhotoIcon className="size-16" style={{ color: colors.iconSecondary }} />
        </div>
      )}
      <div 
        className="absolute inset-0 flex items-center justify-center rounded-full bg-black/50 opacity-0 transition-opacity duration-300 group-hover:opacity-100"
      >
        <CameraIcon className="size-8" style={{ color: colors.iconInverted }} />
      </div>
    </div>
  );
};


// EditableInfoCard component for instructions and description
const EditableInfoCard = ({ 
  icon: Icon, 
  label, 
  value, 
  onEdit,
  onDelete,
  onAdd,
  hasContent,
  className = "",
  fullWidth = false 
}: { 
  icon: React.ElementType;
  label: string;
  value: string | null;
  onEdit: () => void;
  onDelete: () => void;
  onAdd: () => void;
  hasContent: boolean;
  className?: string;
  fullWidth?: boolean;
}) => (
  <div className={`group rounded-xl border border-gray-200 bg-white p-4 shadow-sm transition-all duration-200 hover:shadow-md sm:p-6 lg:p-4 ${fullWidth ? 'col-span-full' : ''} ${className}`}>
    <div className="flex items-start justify-between">
      <div className="flex flex-1 items-start gap-3 sm:gap-4 lg:gap-3">
        <div 
          className="rounded-lg p-2 transition-colors duration-200 group-hover:shadow-sm sm:p-3 lg:p-2"
          style={{ backgroundColor: colors.secondaryLight }}
        >
          <Icon className="size-4 sm:size-5 lg:size-4" style={{ color: colors.secondaryDark }} />
        </div>
        <div className="min-w-0 flex-1">
          <p className="mb-1 text-xs font-medium sm:text-sm lg:text-xs" style={{ color: colors.iconTertiary }}>{label}</p>
          {hasContent ? (
            <p className="break-words text-base font-semibold sm:text-lg lg:text-base" style={{ color: colors.primary }}>
              {value || 'N/A'}
            </p>
          ) : (
            <p className="text-sm italic" style={{ color: colors.iconTertiary }}>
              No {label.toLowerCase()} added yet
            </p>
          )}
        </div>
      </div>
      <div className="flex items-center gap-1">
        {hasContent ? (
          <>
            <button 
              onClick={onEdit}
              className="rounded-lg p-2 transition-all duration-200 hover:bg-gray-100"
              style={{ color: colors.blue500 }}
              title="Edit"
            >
              <PencilIcon className="size-4" />
            </button>
            <button 
              onClick={onDelete}
              className="rounded-lg p-2 transition-all duration-200 hover:bg-gray-100"
              style={{ color: colors.red500 }}
              title="Delete"
            >
              <TrashIcon className="size-4" />
            </button>
          </>
        ) : (
          <button 
            onClick={onAdd}
            className="rounded-lg p-2 transition-all duration-200 hover:bg-gray-100"
            style={{ color: colors.green600 }}
            title="Add"
          >
            <PlusIcon className="size-4" />
          </button>
        )}
      </div>
    </div>
  </div>
);

// --- NEW: AssociatedUsersTable component ---
const AssociatedUsersTable = ({ users }: { users: User[] }) => {
  const getInitials = (firstname?: string, lastname?: string): string => {
    const first = firstname?.charAt(0) || '';
    const last = lastname?.charAt(0) || '';
    return `${first}${last}`.toUpperCase();
  };

  return (
    <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-md">
      {/* Component Header - Compact Desktop */}
      <div className="flex items-center gap-2 p-4 sm:gap-3 sm:p-6 lg:gap-2 lg:p-3" style={{ backgroundColor: colors.card1 }}>
        <UsersIcon className="size-4 sm:size-6 lg:size-4" style={{ color: colors.primaryLight }} />
        <h3 className="text-base font-bold sm:text-xl lg:text-base" style={{ color: colors.primary }}>
          Associated Users ({users.length})
        </h3>
      </div>
      
      {/* Table */}
      <div className="overflow-x-auto">
        <table className="min-w-full text-left">
          <thead style={{ backgroundColor: colors.card1 }}>
            <tr>
              <th scope="col" className="px-3 py-2 text-xs font-semibold sm:px-6 sm:py-4 sm:text-sm lg:px-3 lg:py-2 lg:text-xs" style={{ color: colors.primary }}>
                Name
              </th>
              <th scope="col" className="px-3 py-2 text-xs font-semibold sm:px-6 sm:py-4 sm:text-sm lg:px-3 lg:py-2 lg:text-xs" style={{ color: colors.primary }}>
                Email
              </th>
              <th scope="col" className="px-3 py-2 text-xs font-semibold sm:px-6 sm:py-4 sm:text-sm lg:px-3 lg:py-2 lg:text-xs" style={{ color: colors.primary }}>
                Phone
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 bg-white">
            {users.map((user) => (
              <tr key={user.user_id}>
                <td className="whitespace-nowrap px-3 py-3 sm:px-6 sm:py-4 lg:px-3 lg:py-2">
                  <div className="flex items-center gap-3 sm:gap-4 lg:gap-3">
                    <div 
                      className="flex size-8 shrink-0 items-center justify-center rounded-full text-xs font-bold uppercase text-white sm:size-10 sm:text-sm lg:size-8 lg:text-xs"
                      style={{ backgroundColor: colors.badgePrimary }}
                    >
                      {getInitials(user.people.firstname, user.people.lastname)}
                    </div>
                    <div className="text-xs font-medium sm:text-sm lg:text-xs" style={{ color: colors.primary }}>
                      {user.people.firstname} {user.people.lastname}
                    </div>
                  </div>
                </td>
                <td className="whitespace-nowrap px-3 py-3 text-xs sm:px-6 sm:py-4 sm:text-sm lg:px-3 lg:py-2 lg:text-xs" style={{ color: colors.primaryLight }}>
                  {user.people.email?.toLowerCase() || 'N/A'}
                </td>
                <td className="whitespace-nowrap px-3 py-3 text-xs sm:px-6 sm:py-4 sm:text-sm lg:px-3 lg:py-2 lg:text-xs" style={{ color: colors.primaryLight }}>
                  {user.people.phone || 'N/A'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
// EditModal component for editing instructions and description
const EditModal = ({ 
  isOpen, 
  onClose, 
  onSave, 
  title, 
  initialValue, 
  isSubmitting 
}: { 
  isOpen: boolean; 
  onClose: () => void; 
  onSave: (value: string) => void; 
  title: string; 
  initialValue: string; 
  isSubmitting: boolean; 
}) => {
  const [value, setValue] = useState(initialValue);
  const MAX_CHARACTERS = 500;

  // Update local state when initialValue prop changes
  useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  // Check if content has been modified
  const hasChanges = value !== initialValue;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(value);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = e.target.value;
    if (newValue.length <= MAX_CHARACTERS) {
      setValue(newValue);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-2xl">
        <h3 className="mb-4 text-lg font-semibold" style={{ color: colors.primary }}>
          Edit {title}
        </h3>
        <form onSubmit={handleSubmit}>
          <div className="relative mb-4">
            <textarea
              value={value}
              onChange={handleInputChange}
              className="w-full rounded-lg border border-gray-300 p-3 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
              rows={4}
              placeholder={`Enter ${title.toLowerCase()}...`}
              required
              maxLength={MAX_CHARACTERS}
            />
            <div className="absolute bottom-2 right-2 text-xs text-gray-400">
              {value.length}/{MAX_CHARACTERS}
            </div>
          </div>
          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting || !hasChanges}
              className="rounded-lg px-4 py-2 text-sm font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50"
              style={{
                backgroundColor: hasChanges ? '#2563eb' : '#9ca3af',
                color: 'white'
              }}
            >
              {isSubmitting ? 'Saving...' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// --- END NEW COMPONENT ---

const FacilityInfoPage = () => {
  const { facility: facilityFromContext, user } = useContext(FacilityContext);
  const [facilityImage, setFacilityImage] = useState<string | null>(null);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  
  // Facility details state
  const [facilityDetails, setFacilityDetails] = useState<FacilityDetails | null>(null);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);
  const [detailsError, setDetailsError] = useState<string | null>(null);
  
  // Edit modal state
  const [editModal, setEditModal] = useState<{
    isOpen: boolean;
    type: 'instructions' | 'description' | null;
    value: string;
  }>({
    isOpen: false,
    type: null,
    value: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const api = useApiService();

  // Fetch facility details
  const fetchFacilityDetails = async () => {
    if (!facilityFromContext?.facility_id) return;
    
    setIsLoadingDetails(true);
    setDetailsError(null);
    
    try {
      const response = await api.get<FacilityDetails>(`/facilities/facility-details?facilityId=${facilityFromContext.facility_id}`);
      setFacilityDetails(response.data);
    } catch (error: any) {
      if (error.response?.status === 404) {
        // Facility details don't exist yet, that's okay
        setFacilityDetails(null);
      } else {
        setDetailsError(error.response?.data?.message || 'Failed to fetch facility details');
      }
    } finally {
      setIsLoadingDetails(false);
    }
  };

  // Create or update facility details
  const saveFacilityDetails = async (type: 'instructions' | 'description', value: string) => {
    if (!facilityFromContext?.facility_id || !user?.user_id) return;
    
    setIsSubmitting(true);
    
    try {
      if (facilityDetails) {
        // Update existing details
        const response = await api.patch<FacilityDetails>(`/facilities/facility-details?id=${facilityDetails.facility_detail_id}`, {
          [type]: value,
          updated_by: user.user_id
        });
        setFacilityDetails(response.data);
      } else {
        // Create new details
        const response = await api.post<FacilityDetails>('/facilities/facility-details', {
          facility_id: facilityFromContext.facility_id,
          instructions: type === 'instructions' ? value : null,
          description: type === 'description' ? value : null,
          created_by: user.user_id
        });
        setFacilityDetails(response.data);
      }
      
      setEditModal({ isOpen: false, type: null, value: '' });
    } catch (error: any) {
      setDetailsError(error.response?.data?.message || 'Failed to save facility details');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Delete individual field (instructions or description)
  const deleteField = async (type: 'instructions' | 'description') => {
    if (!facilityDetails?.facility_detail_id || !user?.user_id) return;
    
    const fieldName = type === 'instructions' ? 'instructions' : 'description';
    if (!window.confirm(`Are you sure you want to delete the ${fieldName}? This action cannot be undone.`)) {
      return;
    }
    
    try {
      // Update the facility details by setting the specific field to null
      const response = await api.patch<FacilityDetails>(`/facilities/facility-details?id=${facilityDetails.facility_detail_id}`, {
        [type]: null,
        updated_by: user.user_id
      });
      setFacilityDetails(response.data);
    } catch (error: any) {
      setDetailsError(error.response?.data?.message || `Failed to delete ${fieldName}`);
    }
  };


  // Handle edit modal actions
  const handleEdit = (type: 'instructions' | 'description') => {
    const currentValue = facilityDetails?.[type] || '';
    setEditModal({
      isOpen: true,
      type,
      value: currentValue
    });
  };

  const handleAdd = (type: 'instructions' | 'description') => {
    setEditModal({
      isOpen: true,
      type,
      value: ''
    });
  };

  const handleSave = (value: string) => {
    if (editModal.type) {
      saveFacilityDetails(editModal.type, value);
    }
  };

  const { data: facilityDetailsResponse, isLoading, error } = useOne<Facility>({
    resource: 'facilities',
    id: facilityFromContext?.facility_id || '',
    queryOptions: {
      enabled: !!facilityFromContext?.facility_id,
    },
  });

  const facility = facilityDetailsResponse?.data;

  // Fetch facility details when facility is loaded
  useEffect(() => {
    if (facility?.facility_id) {
      fetchFacilityDetails();
    }
  }, [facility?.facility_id]);

  // Handle image upload (unchanged)
  const handleImageUpload = async (file: File) => {
    setIsUploadingImage(true);
    setUploadError(null);
    
    try {
      const tempUrl = URL.createObjectURL(file);
      setFacilityImage(tempUrl);
      
      const formData = new FormData();
      formData.append('file', file);

      const response = await api.post(`/facilities/modify-logo/${facility?.facility_id}`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      const newLogoUrl = response.data.logo || response.data.image || response.data.url;
      setFacilityImage(newLogoUrl);
      
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Failed to upload image. Please try again.';
      setUploadError(errorMessage);
      setFacilityImage(facility?.logo || null);
    } finally {
      setIsUploadingImage(false);
    }
  };

  if (isLoading || !facilityFromContext) {
    return <Loading />;
  }

  if (error) {
    return (
      <div className="flex h-full flex-col items-center justify-center p-8 text-center">
        <ExclamationTriangleIcon className="mb-4 size-16" style={{ color: colors.red500 }} />
        <h3 className="mb-2 text-2xl font-bold" style={{ color: colors.red700 }}>Error Loading Data</h3>
        <p className="text-lg" style={{ color: colors.iconTertiary }}>
          Could not fetch the facility details. Please try again later.
        </p>
      </div>
    );
  }

  if (!facility) {
    return (
      <div className="flex h-full flex-col items-center justify-center p-8 text-center">
        <BuildingOffice2Icon className="mb-4 size-16" style={{ color: colors.iconTertiary }} />
        <h3 className="mb-2 text-2xl font-bold" style={{ color: colors.primary }}>Facility Not Found</h3>
        <p className="text-lg" style={{ color: colors.iconTertiary }}>
          The requested facility could not be found.
        </p>
      </div>
    );
  }
  
  const getFullAddress = (addr: { address1: string, address2: string | null, city: string, state: string, zip: string }) => {
    return [addr.address1, addr.address2, addr.city, addr.state, addr.zip].filter(Boolean).join(', ');
  };

  const facilityAddress = getFullAddress(facility);
  const orgAddress = facility.organizations ? getFullAddress(facility.organizations) : "No address provided";
  
  const displayImage = facilityImage || facility.logo;

  return (
    <div className="min-h-screen" style={{ backgroundColor: colors.card1 }}>
      {/* Header Section (unchanged) */}
      <div className="relative overflow-hidden rounded-xl" style={{ backgroundColor: colors.primary }}>
        <div className="absolute inset-0 bg-gradient-to-r from-transparent to-black/10 rounded-xl"></div>
        <div className="relative mx-auto max-w-6xl px-4 py-4 sm:px-6 sm:py-6 lg:px-6 lg:py-4">
          <div className="flex flex-col items-center gap-4 md:flex-row sm:gap-6 lg:gap-4">
            <div className="relative">
              <ImageUpload
                currentImage={displayImage}
                facilityName={facility.facility_name}
                onImageChange={handleImageUpload}
              />
              {isUploadingImage && (
                <div className="absolute inset-0 flex items-center justify-center rounded-full bg-black/50">
                  <div className="size-8 animate-spin rounded-full border-b-2 border-white"></div>
                </div>
              )}
            </div>
            <div className="flex-1 text-center md:text-left">
              <h1 
                className="mb-1 text-2xl font-bold sm:text-3xl lg:text-xl"
                style={{
                  background: 'linear-gradient(to right, rgb(245, 185, 87), rgb(255, 237, 205), rgb(171, 109, 10))',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                  fontFamily: 'Inter, sans-serif'
                }}
              >
                {facility.facility_name}
              </h1>
              <div className="mb-3 flex items-center justify-center gap-2 md:justify-start sm:gap-3 lg:gap-2">
                <BuildingOffice2Icon className="size-4 sm:size-5 lg:size-4" style={{ color: colors.secondaryLight }} />
                <span className="text-base sm:text-lg lg:text-base" style={{ color: colors.secondaryLight }}>
                  {facility.organizations?.organization_name || 'No Organization'}
                </span>
              </div>
              <StatusBadge status={facility.status} />
              {uploadError && (
                <div className="mt-4 flex items-center gap-2 text-sm" style={{ color: colors.red700 }}>
                  <ExclamationCircleIcon className="size-4" />
                  {uploadError}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Main Content - Compact Desktop */}
      <div className="mx-auto max-w-6xl px-4 py-4 sm:px-6 sm:py-6 lg:px-6 lg:py-4">
        <div className="grid grid-cols-1 gap-4 sm:gap-6 lg:grid-cols-3 lg:gap-4">
          {/* Contact Information (unchanged) */}
          <div className="lg:col-span-1">
            <SectionHeader 
              title="Contact Information" 
              subtitle="Get in touch with the facility"
              icon={PhoneIcon} 
            />
            <div className="space-y-3 sm:space-y-4 lg:space-y-3">
              <InfoCard 
                icon={EnvelopeIcon}
                label="Email Address"
                value={facility.email}
                copyable={true}
              />
              <InfoCard 
                icon={PhoneIcon}
                label="Phone Number"
                value={facility.phone}
                copyable={true}
              />
              <InfoCard 
                icon={MapPinIcon}
                label="Facility Address"
                value={facilityAddress}
                copyable={true}
                fullWidth={true}
              />
            </div>
          </div>

          {/* Facility Details (unchanged) */}
          <div className="lg:col-span-2">
            <SectionHeader 
              title="Facility Details" 
              subtitle="Technical information and identifiers"
              icon={BuildingOffice2Icon} 
            />
            <div className="grid grid-cols-1 gap-3 sm:gap-4 md:grid-cols-2 lg:gap-3">
              <InfoCard 
                icon={HashtagIcon}
                label="Facility ID"
                value={facility.facility_id}
                copyable={true}
              />
              <InfoCard 
                icon={MapIcon}
                label="Location Code"
                value={facility.location_code}
                copyable={true}
              />
              <InfoCard 
                icon={GlobeAltIcon}
                label="Market ID"
                value={facility.market_id}
                copyable={true}
              />
              <InfoCard 
                icon={BuildingOffice2Icon}
                label="Facility Type ID"
                value={facility.facility_type_id}
                copyable={true}
              />
              <InfoCard 
                icon={MapPinIcon}
                label="Latitude"
                value={facility.lat}
                copyable={true}
              />
              <InfoCard 
                icon={MapPinIcon}
                label="Longitude"
                value={facility.long}
                copyable={true}
              />
            </div>
          </div>
        </div>

        {/* Facility Instructions & Description */}
        <div className="mt-6 sm:mt-12 lg:mt-6">
          <SectionHeader 
            title="Facility Instructions & Description" 
            subtitle="Important information and guidelines for the facility"
            icon={BuildingOffice2Icon} 
          />
          
          {detailsError && (
            <div className="mb-4 flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm" style={{ color: colors.red700 }}>
              <ExclamationTriangleIcon className="size-4" />
              {detailsError}
            </div>
          )}
          
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <EditableInfoCard 
              icon={ClipboardDocumentIcon}
              label="Instructions"
              value={facilityDetails?.instructions || null}
              onEdit={() => handleEdit('instructions')}
              onDelete={() => deleteField('instructions')}
              onAdd={() => handleAdd('instructions')}
              hasContent={!!(facilityDetails?.instructions && facilityDetails.instructions.trim() !== '')}
              fullWidth={true}
            />
            <EditableInfoCard 
              icon={ClipboardDocumentIcon}
              label="Description"
              value={facilityDetails?.description || null}
              onEdit={() => handleEdit('description')}
              onDelete={() => deleteField('description')}
              onAdd={() => handleAdd('description')}
              hasContent={!!(facilityDetails?.description && facilityDetails.description.trim() !== '')}
              fullWidth={true}
            />
          </div>
          
          {isLoadingDetails && (
            <div className="mt-4 flex items-center justify-center rounded-lg border border-gray-200 bg-white p-4">
              <div className="size-6 animate-spin rounded-full border-2 border-gray-300 border-t-blue-600"></div>
              <span className="ml-2 text-sm text-gray-600">Loading facility details...</span>
            </div>
          )}
        </div>

        {/* Organization Details (unchanged) */}
        {facility.organizations && (
          <div className="mt-12">
            <SectionHeader 
              title="Organization Details" 
              subtitle="Parent organization information"
              icon={UserIcon} 
            />
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <InfoCard 
                icon={BuildingOffice2Icon}
                label="Organization Name"
                value={facility.organizations.organization_name}
                copyable={true}
              />
              <InfoCard 
                icon={HashtagIcon}
                label="Organization ID"
                value={facility.organizations.organization_id}
                copyable={true}
              />
              <InfoCard 
                icon={EnvelopeIcon}
                label="Organization Email"
                value={facility.organizations.email}
                copyable={true}
              />
              <InfoCard 
                icon={PhoneIcon}
                label="Organization Phone"
                value={facility.organizations.phone}
                copyable={true}
              />
              <InfoCard 
                icon={MapPinIcon}
                label="Organization Address"
                value={orgAddress}
                copyable={true}
                fullWidth={true}
              />
            </div>
          </div>
        )}

        {/* --- MODIFIED: Facility Users Section - Compact Desktop --- */}
        {facility.users && facility.users.length > 0 && (
          <div className="mt-6 sm:mt-12 lg:mt-6">
            <AssociatedUsersTable users={facility.users} />
          </div>
        )}
        {/* --- END MODIFICATION --- */}

        {/* Edit Modal */}
        <EditModal
          key={`${editModal.type}-${editModal.isOpen}`} // Force remount when type changes
          isOpen={editModal.isOpen}
          onClose={() => setEditModal({ isOpen: false, type: null, value: '' })}
          onSave={handleSave}
          title={editModal.type === 'instructions' ? 'Instructions' : 'Description'}
          initialValue={editModal.value}
          isSubmitting={isSubmitting}
        />
      </div>
    </div>
  );
};

export default FacilityInfoPage;